/**
 * Snapshot do Dashboard — client-side via html2canvas + jsPDF.
 *
 * Respeita 100% os filtros ativos porque captura EXATAMENTE o que está
 * renderizado no navegador do usuário autenticado. Nada trafega pelo
 * backend, nenhuma requisição adicional é feita — não há risco multi-tenant.
 *
 * Elementos com `data-snapshot-hide` são temporariamente ocultados durante
 * a captura (botões, menus, tooltips).
 */
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const HIDE_SELECTOR = "[data-snapshot-hide]";

async function _rasterize(node, scale = 2) {
  // Oculta elementos anotados durante a captura
  const hidden = Array.from(node.querySelectorAll(HIDE_SELECTOR));
  const prev = hidden.map((el) => el.style.visibility);
  hidden.forEach((el) => { el.style.visibility = "hidden"; });
  try {
    const canvas = await html2canvas(node, {
      scale,
      useCORS: true,
      allowTaint: false,
      backgroundColor: "#F7F9F8",
      logging: false,
      windowWidth: node.scrollWidth,
      windowHeight: node.scrollHeight,
    });
    return canvas;
  } finally {
    hidden.forEach((el, i) => { el.style.visibility = prev[i] || ""; });
  }
}

function _fmtNow() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return {
    file: `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`,
    human: `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

/** Exporta PNG em alta resolução (scale 2x). */
export async function exportDashboardPng({ nodeId = "dashboard-snapshot", companyLabel = "" } = {}) {
  const node = document.getElementById(nodeId);
  if (!node) throw new Error(`Nó #${nodeId} não encontrado`);
  const canvas = await _rasterize(node, 2);
  const ts = _fmtNow();
  const a = document.createElement("a");
  a.download = `dashboard-rh_${companyLabel ? companyLabel.replace(/\s+/g, "-").toLowerCase() + "_" : ""}${ts.file}.png`;
  a.href = canvas.toDataURL("image/png", 1.0);
  a.click();
}

/** Exporta PDF A4 paisagem com cabeçalho. */
export async function exportDashboardPdf({
  nodeId = "dashboard-snapshot",
  title = "Dashboard Executivo de RH",
  companyLabel = "",
  periodLabel = "",
} = {}) {
  const node = document.getElementById(nodeId);
  if (!node) throw new Error(`Nó #${nodeId} não encontrado`);
  const canvas = await _rasterize(node, 2);

  // A4 paisagem = 297 x 210 mm
  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();  // 297
  const pageH = pdf.internal.pageSize.getHeight(); // 210
  const marginX = 8;
  const headerH = 14;
  const footerH = 8;

  // Cabeçalho
  pdf.setFillColor(6, 78, 59);
  pdf.rect(0, 0, pageW, headerH, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text(title, marginX, 9);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  const meta = [companyLabel && `Empresa: ${companyLabel}`,
                periodLabel && `Período: ${periodLabel}`,
                `Gerado em ${_fmtNow().human}`].filter(Boolean).join("   •   ");
  pdf.text(meta, pageW - marginX, 9, { align: "right" });

  // Imagem do dashboard (ajusta proporcionalmente)
  const imgData = canvas.toDataURL("image/png", 1.0);
  const availableW = pageW - marginX * 2;
  const availableH = pageH - headerH - footerH - 6;
  const ratio = canvas.width / canvas.height;
  let drawW = availableW;
  let drawH = drawW / ratio;
  if (drawH > availableH) {
    drawH = availableH;
    drawW = drawH * ratio;
  }
  const x = (pageW - drawW) / 2;
  const y = headerH + 4;
  pdf.addImage(imgData, "PNG", x, y, drawW, drawH, undefined, "FAST");

  // Rodapé
  pdf.setTextColor(120, 120, 120);
  pdf.setFontSize(7);
  pdf.text("TheoKratos RH · Centro de Inteligência de RH", marginX, pageH - 3);
  pdf.text("Página 1 de 1", pageW - marginX, pageH - 3, { align: "right" });

  const ts = _fmtNow();
  pdf.save(`dashboard-rh_${companyLabel ? companyLabel.replace(/\s+/g, "-").toLowerCase() + "_" : ""}${ts.file}.pdf`);
}
